import React, {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {Event, pubSub} from 'components/customer/RoomPlanner/helper/PubSub';
import {submitProductEditorAndContinue} from 'components/customer/RoomPlanner/helper/submitProductEditor';
import {useJobContext} from 'contexts/JobContext';
import {PartialJob} from 'shared/types/PartialJob';
import {
    setPreviewPosition,
    getIsTopView,
    getIsEditHeightView,
    setIsTopView,
    setIsEditingRoomDimension,
    setIsRoomPlannerEditing,
    getIsDeleting,
    setOldCabinetPosition,
    getSelectedMenu,
    selectedMenuSet,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {store, useAppDispatch, useAppSelector} from 'store/customer';
import {useDeleteLayoutMutation} from 'components/customer/RoomPlanner/store/roomPlannerApi';
import {Loader} from 'shared/helpers';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';
import {isMobile} from 'react-device-detect';
import {useUnsavedChangesDialog} from 'shared/components/ConfirmationDialog/UnsavedChangesDialog';
import DeleteRoomLayoutDialog from 'components/customer/RoomPlanner/components/Menu/DeleteRoomLayoutDialog';

export enum MenuItem {
    ADD_PRODUCT = 'ADD_PRODUCT',
    ADD_APPLIANCE = 'ADD_APPLIANCE',
    HARDWARE_SELECTIONS = 'HARDWARE_SELECTIONS',
    MATERIALS = 'MATERIALS',
    EDIT_ROOM = 'EDIT_ROOM',
    EXIT_ROOM_PLANNER = 'EXIT_ROOM_PLANNER',
    CLEAR_LAYOUT = 'CLEAR_LAYOUT',
}

export enum PreviewType {
    PREVIEW_3D,
    PREVIEW_EDGE,
    PREVIEW_DOOR,
    PREVIEW_DRAWER,
    PREVIEW_SHELVES,
}

interface MenuContext {
    selectedMenu?: string;
    handleClick?: (event: string) => () => void;
    hideMenu?: () => void;
    preview?: React.ReactNode;
    previewDoor?: React.ReactNode;
    previewEdge?: React.ReactNode;
    previewDrawer?: React.ReactNode;
    previewShelves?: React.ReactNode;
    previewType?: PreviewType;
    closeProductEditorMenu?: (yesHandler?: () => void) => void;
}

const MenuContext = createContext<MenuContext>({});

const useMenuContext = () => useContext(MenuContext);

const MenuProvider = ({children}: PropsWithChildren<object>) => {
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const {jobId, roomId} = useParams();
    const navigate = useNavigate();
    const {setIsRoomPlanner} = useJobContext() as PartialJob;

    const [preview, setPreview] = useState<React.ReactNode>();
    const [previewEdge, setPreviewEdge] = useState<React.ReactNode>();
    const [previewDoor, setPreviewDoor] = useState<React.ReactNode>();
    const [previewDrawer, setPreviewDrawer] = useState<React.ReactNode>();
    const [previewShelves, setPreviewShelves] = useState<React.ReactNode>();
    const [previewType, setPreviewType] = useState<PreviewType>(
        PreviewType.PREVIEW_3D
    );

    const selectedMenu = useAppSelector(getSelectedMenu);
    const isTopView = useAppSelector(getIsTopView);
    const isEditHeightView = useAppSelector(getIsEditHeightView);
    const isDeleting = useAppSelector(getIsDeleting);
    const menuAutoOpened = useRef(false);

    const cabinetIdReference = useRef<string | null>(null);
    const previousUrlSelectedRef = useRef<string | null>(null);
    const {dialog, showDialog} = useUnsavedChangesDialog();
    const [showDeleteLayoutDialog, setShowDeleteLayoutDialog] = useState(false);
    const [deleteLayout, {isLoading: isDeletingLayout}] =
        useDeleteLayoutMutation();

    const confirmDeleteLayout = useCallback(() => {
        setShowDeleteLayoutDialog(false);
        void deleteLayout(Number(roomId))
            .unwrap()
            .then(() => {
                window.location.reload();
            });
    }, [deleteLayout, roomId]);

    const cancelDeleteLayout = useCallback(() => {
        setShowDeleteLayoutDialog(false);
    }, []);

    const {setSelectedCabinetGroup, selectedCabinetGroup} =
        useRoomPlannerContext();

    const {cabinetGroupListRef} = useRoomPlannerContext();

    const setSelectedMenu = useCallback(
        (menu: string | ((menu: string) => string)) => {
            if (typeof menu === 'string') {
                dispatch(selectedMenuSet(menu));
            } else {
                dispatch(selectedMenuSet(menu(selectedMenu)));
            }
        },
        [selectedMenu]
    );

    const handleClick = useCallback(
        (event: string) => () => {
            switch (event) {
                case MenuItem.EXIT_ROOM_PLANNER:
                    if (isDeleting) {
                        return;
                    }
                    void navigate(`/v2/job/${jobId}/room/${roomId}/dashboard`);
                    break;
                case MenuItem.EDIT_ROOM:
                    setSelectedCabinetGroup?.(null);
                    dispatch(setPreviewPosition('TOP'));
                    dispatch(setIsTopView(true));
                    dispatch(setIsEditingRoomDimension(true));
                    dispatch(
                        setOldCabinetPosition(
                            cabinetGroupListRef.current.map((cabinet) => ({
                                id: String(
                                    cabinet.userData?.values?.duplicate_id
                                ),
                                position: {
                                    x: cabinet.position.x,
                                    y: cabinet.position.y,
                                    z: cabinet.position.z,
                                },
                                rotation: {
                                    x: cabinet.rotation.x,
                                    y: cabinet.rotation.y,
                                    z: cabinet.rotation.z,
                                },
                            }))
                        )
                    );
                    break;
                case MenuItem.CLEAR_LAYOUT:
                    setShowDeleteLayoutDialog(true);
                    break;
                default: {
                    const nextMenu = selectedMenu == event ? '' : event;
                    if (nextMenu !== selectedMenu) {
                        const next = new URLSearchParams();
                        if (nextMenu === MenuItem.ADD_APPLIANCE) {
                            next.set('category', 'placeholder');
                        }
                        setSearchParams(next);
                    }
                    if (selectedCabinetGroup) {
                        setSelectedCabinetGroup?.(null);
                        dispatch(setIsRoomPlannerEditing(false));
                    }
                    setSelectedMenu(nextMenu);
                    break;
                }
            }
        },
        [
            jobId,
            roomId,
            dispatch,
            isDeleting,
            selectedCabinetGroup,
            selectedMenu,
            setSearchParams,
            setSelectedMenu,
        ]
    );

    const hideMenu = () => {
        setSelectedMenu('');
        cabinetIdReference.current = null;
        setSearchParams({});
        // Reset editing state and clear active group when sidebar is closed
        dispatch(setIsRoomPlannerEditing(false));
        setSelectedCabinetGroup?.(null);
    };

    useEffect(() => {
        if (menuAutoOpened.current) {
            return;
        }

        if (!isTopView && !isEditHeightView && !isMobile) {
            setSelectedMenu(MenuItem.ADD_PRODUCT);
            menuAutoOpened.current = true;
        }
    }, [isTopView, isEditHeightView, isMobile]);

    const closeProductEditorMenuHandler = () => {
        /*
         * Dismiss the sidebar entirely. Keeping ADD_PRODUCT / ADD_APPLIANCE
         * after editor close left the category catalog stuck open, and the
         * catalog X reused this handler so it could not close either.
         */
        productEvents.dropSelectionParams();
        hideMenu();
    };

    const closeProductEditorMenu = (yesHandler?: () => void) => {
        // Read at call time rather than from the render: an editor that saves
        // clears the flag in the same tick it asks to be closed.
        if (store.getState().productData.productUpdated) {
            showDialog({
                onConfirmDiscard: () => {
                    yesHandler ? yesHandler() : closeProductEditorMenuHandler();
                },
                onConfirmSave: async () => {
                    await submitProductEditorAndContinue(
                        yesHandler ?? closeProductEditorMenuHandler
                    );
                },
            });
        } else {
            closeProductEditorMenuHandler();
        }
    };

    useEffect(() => {
        const previewHandler = (data: React.ReactNode) => {
            setPreview(data);
        };

        const previewHandlerDoor = (data: React.ReactNode) => {
            setPreviewDoor(data);
        };

        const previewHandlerEdge = (data: React.ReactNode) => {
            setPreviewEdge(data);
        };

        const previewHandlerDrawer = (data: React.ReactNode) => {
            setPreviewDrawer(data);
        };

        const previewHandlerShelves = (data: React.ReactNode) => {
            setPreviewShelves(data);
        };

        const previewTypeHandler = (data: PreviewType) => {
            setPreviewType(data);
        };

        const resetSelectedMenu = (wasEditing = false) => {
            if (wasEditing) {
                setSelectedMenu('');
                // Reset editing state when canceling
                dispatch(setIsRoomPlannerEditing(false));
                setSelectedCabinetGroup?.(null);
            } else {
                if (searchParams.get('product')) {
                    searchParams.delete('product');

                    setSearchParams(searchParams);
                }
            }
        };

        const setSelectedMenuHandler = (menu: MenuItem) => {
            setSelectedMenu(menu);
        };

        pubSub.subscribe(Event.ROOM_PLANNER_3D_PREVIEW, previewHandler);
        pubSub.subscribe(Event.ROOM_PLANNER_DOOR_PREVIEW, previewHandlerDoor);
        pubSub.subscribe(Event.ROOM_PLANNER_EDGE_PREVIEW, previewHandlerEdge);
        pubSub.subscribe(
            Event.ROOM_PLANNER_DRWAWER_PREVIEW,
            previewHandlerDrawer
        );
        pubSub.subscribe(
            Event.ROOM_PLANNER_SHELVES_PREVIEW,
            previewHandlerShelves
        );
        pubSub.subscribe(Event.ROOM_PLANNER_PREVIEW_TYPE, previewTypeHandler);
        pubSub.subscribe(Event.RESET_SELECTED_MENU, resetSelectedMenu);
        pubSub.subscribe(Event.SET_SELECT_MENU, setSelectedMenuHandler);

        return () => {
            pubSub.unsubscribe(Event.ROOM_PLANNER_3D_PREVIEW, previewHandler);
            pubSub.unsubscribe(
                Event.ROOM_PLANNER_DOOR_PREVIEW,
                previewHandlerDoor
            );
            pubSub.unsubscribe(
                Event.ROOM_PLANNER_EDGE_PREVIEW,
                previewHandlerEdge
            );
            pubSub.unsubscribe(
                Event.ROOM_PLANNER_DRWAWER_PREVIEW,
                previewHandlerDrawer
            );
            pubSub.unsubscribe(
                Event.ROOM_PLANNER_SHELVES_PREVIEW,
                previewHandlerShelves
            );
            pubSub.unsubscribe(
                Event.ROOM_PLANNER_PREVIEW_TYPE,
                previewTypeHandler
            );
            pubSub.unsubscribe(Event.RESET_SELECTED_MENU, resetSelectedMenu);
            pubSub.unsubscribe(Event.SET_SELECT_MENU, setSelectedMenuHandler);
        };
    }, [dispatch, searchParams, setSearchParams]);

    useEffect(() => {
        setIsRoomPlanner(true);

        return () => {
            setIsRoomPlanner(false);
        };
    }, []);

    useEffect(() => {
        const cabinetId = searchParams.get('cabinetId');
        const preventReset = searchParams.get('preventReset');
        const isProductEdit = !!cabinetId && !!preventReset;
        const isPlaceholderEditor =
            !!searchParams.get('placeholderEdit') ||
            !!searchParams.get('placeholderFace');
        const selected = searchParams.get('selected');
        const previousSelected = previousUrlSelectedRef.current;
        previousUrlSelectedRef.current = selected;

        if (isPlaceholderEditor) {
            setSelectedMenu(MenuItem.ADD_APPLIANCE);
            return;
        }
        if (isProductEdit) {
            setSelectedMenu(MenuItem.ADD_PRODUCT);
            return;
        }

        /*
         * Close the catalog when a 3D cabinet is newly selected. A leftover
         * `selected` param from the cabinet we just deselected must not
         * immediately close the appliance catalog we are opening.
         */
        if (
            selected &&
            selected != cabinetId &&
            selected !== previousSelected
        ) {
            setSelectedMenu('');
        }
    }, [searchParams]);

    return (
        <MenuContext.Provider
            value={{
                selectedMenu,
                handleClick,
                hideMenu,
                preview,
                previewEdge,
                previewDoor,
                previewDrawer,
                previewShelves,
                previewType,
                closeProductEditorMenu,
            }}>
            <Loader loader={isDeletingLayout} />
            {dialog}
            <DeleteRoomLayoutDialog
                show={showDeleteLayoutDialog}
                onConfirm={confirmDeleteLayout}
                onCancel={cancelDeleteLayout}
            />
            {children}
        </MenuContext.Provider>
    );
};

export {MenuContext, useMenuContext, MenuProvider};
